import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { FiSearch } from "react-icons/fi";
import { LuFileSearch } from "react-icons/lu";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import DashboardTable from "../DashboardTable";
import UserList from "../users/UserList";
import ReceiptsList from "../receipts/ReceiptsList";
import PrizesList from "../prizes/PrizesList";
import BusinessWinnersTable from "../business_winners/BusinessWinnersTable";
import FilterPopUp from "./FilterPopUp";
import ExportXLS from "../ExportXLS";
import UserDetails from "../users/UserDetails";
import WinnerDetails from "../prizes/WinnerDetails";
import ReceiptView from "../ReceiptView";
import GameDetails from "../game_rules/GameDetails";
import ReactModal from "../ReactModal";

import ReportsTabs from "./ReportsTabs";
import ReportsToolbar from "./ReportsToolbar";
import StatisticsPanel from "./StatisticsPanel";

import {
    useLazyGetFilteredReceiptsQuery,
    useLazyGetFilteredUsersQuery,
    useLazyGetFilteredWinnersQuery,
    useLazyGetFilteredBusinessWinnersQuery,
} from "../../store/services/reportsServices";
import { exportXLSModalActions } from "../../store/exportXLSModalSlice";

const PAGE_LIMIT = 10;
const NON_FILTER_KEYS = ["page", "category", "sort_by", "sort_order", "q", "start_date", "previous_month"];

const HEAD_LABELS = {
    users: [
        { name: "ID", sort_value: "id" },
        { name: "Name & Surname", sort_value: "first_name" },
        { name: "Created Date", sort_value: "created_at" },
        { name: "Phone number", sort_value: "phone_number" },
        { name: "Email", sort_value: "email" },
        { name: "Role", sort_value: "role" },
    ],
    receipts: [
        { name: "ID", sort_value: "id" },
        { name: "Submission Date", sort_value: "created_at" },
        { name: "User", sort_value: "first_name" },
        { name: "NUI", sort_value: "NUI" },
        { name: "Total with VAT", sort_value: "total_with_vat" },
        { name: "Status", sort_value: "" },
    ],
    winners: [
        { name: "ID", sort_value: "id" },
        { name: "Name & Surname", sort_value: "first_name" },
        { name: "Email", sort_value: "email" },
        { name: "Game", sort_value: "title" },
        { name: "Receipt Count", sort_value: "receipt_count" },
        { name: "Date", sort_value: "created_at" },
    ],
};

const generateCurrentMonth = () => {
    const d = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const deriveScope = (params) => {
    if (params.previous_month) return "previous_month";
    if (params.start_date) return "current_month";
    return "all";
};

export default function ReportsMain() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isFirstRender = useRef(true);

    const [params, setParams] = useState({});
    const [filters, setFilters] = useState({});
    const [data, setData] = useState({ data: [], currentPage: 1, totalPages: 1, totalCount: 0 });
    const [searchTextInput, setSearchTextInput] = useState("");
    const [selectedGame, setSelectedGame] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const [getReceiptsData, { isLoading: isReceiptsLoading, isFetching: isReceiptsFetching }] = useLazyGetFilteredReceiptsQuery();
    const [getUsersData, { isLoading: isUsersLoading, isFetching: isUsersFetching }] = useLazyGetFilteredUsersQuery();
    const [getWinnersData, { isLoading: isWinnersLoading, isFetching: isWinnersFetching }] = useLazyGetFilteredWinnersQuery();
    const [getBusinessWinnersData, { isLoading: isBusinessWinnersLoading, isFetching: isBusinessWinnersFetching }] = useLazyGetFilteredBusinessWinnersQuery();

    const isExportXLSOpen = useSelector((state) => state.export_xls_modal.isOpen);
    const isUserDetailsOpen = useSelector((state) => state.user_details.isOpen);
    const isWinnerDetailsOpen = useSelector((state) => state.winner_details.isOpen);
    const isReceiptViewOpen = useSelector((state) => state.receipt_view.isOpen);
    const { isOpen: isGameDetailsOpen } = useSelector((state) => state.game_details);

    const category = params.category || "users";
    const isStatistics = category === "statistics";
    const scope = deriveScope(params);

    /* Hydrate from URL */
    useEffect(() => {
        const newParams = {};
        searchParams.forEach((v, k) => (newParams[k] = v));
        if (newParams.previous_month === "true") newParams.previous_month = true;
        setParams(newParams);
        const { page, category: _c, ...rest } = newParams;
        setFilters(rest);
        if (newParams.q) setSearchTextInput(newParams.q);
    }, [searchParams]);

    /* Fetch data + sync URL whenever params change */
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const queryString = Object.entries(params)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join("&");
        /* Replace, not push: this only mirrors params into the URL, and pushing
           stacked duplicate entries that took several Back clicks to leave */
        navigate(`/reports?${queryString}`, { replace: true });

        const fetchData = async () => {
            try {
                if (!params.category || params.category === "users") {
                    const res = await getUsersData(params).unwrap();
                    setData(res);
                } else if (params.category === "receipts") {
                    const res = await getReceiptsData({ params }).unwrap();
                    setData(res);
                } else if (params.category === "winners") {
                    const res = await getWinnersData({ params }).unwrap();
                    setData(res);
                } else if (params.category === "business_winners") {
                    const res = await getBusinessWinnersData({ params }).unwrap();
                    setData(res);
                }
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        if (params.category !== "statistics") fetchData();
    }, [params, navigate, getUsersData, getReceiptsData, getWinnersData, getBusinessWinnersData]);

    /* Derived state */
    const activeFilterCount = useMemo(
        () => Object.keys(filters).filter((k) => !NON_FILTER_KEYS.includes(k)).length,
        [filters]
    );

    const currentPage = parseInt(params.page) || 1;
    const totalCount = data?.data?.totalCount ?? 0;
    const totalPages = data?.data?.totalPages ?? 1;
    const rows = data?.data?.data || [];

    const isFetching =
        isUsersFetching || isReceiptsFetching || isWinnersFetching || isBusinessWinnersFetching;
    const isLoading = isUsersLoading || isReceiptsLoading || isWinnersLoading || isBusinessWinnersLoading;
    const isEmpty = !isLoading && !isFetching && rows.length === 0;

    const rangeLabel = useMemo(() => {
        if (!totalCount) return "";
        const start = (currentPage - 1) * PAGE_LIMIT + 1;
        const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
        return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
    }, [currentPage, totalCount]);

    const subtitle = useMemo(() => {
        if (isStatistics) return "Statistikat e lojës së zgjedhur.";
        if (totalCount) return `${totalCount.toLocaleString()} rezultate`;
        return "Gjenero raporte për përdoruesit, kuponat dhe fituesit.";
    }, [isStatistics, totalCount]);

    /* Handlers */
    const handleTabChange = (tabId) => {
        if (tabId === "statistics") {
            setParams({ category: "statistics" });
            return;
        }
        const next = { category: tabId, page: 1 };
        setParams(next);
        setFilters({});
        setSearchTextInput("");
    };

    const handleScopeChange = (scopeId) => {
        const next = { ...params, page: 1 };
        delete next.start_date;
        delete next.previous_month;
        if (scopeId === "current_month") next.start_date = generateCurrentMonth();
        if (scopeId === "previous_month") next.previous_month = true;
        setParams(next);
    };

    const debounceRef = useRef(null);
    const handleSearchTextInputChange = (e) => {
        const text = e.target.value;
        setSearchTextInput(text);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (text.length > 2) {
            debounceRef.current = setTimeout(() => {
                setParams((p) => ({ ...p, q: text, page: 1 }));
            }, 400);
        } else {
            setParams((p) => {
                const { q, ...rest } = p;
                return rest;
            });
        }
    };

    const handleSort = (value) => {
        setParams((p) => {
            if (p.sort_by === value && p.sort_order === "asc") {
                const { sort_by, sort_order, ...rest } = p;
                return rest;
            }
            return {
                ...p,
                sort_by: value,
                sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
            };
        });
    };

    const handleClearSort = () => {
        setParams((p) => {
            const { sort_by, sort_order, ...rest } = p;
            return rest;
        });
    };

    const handlePageChange = (page) => {
        setParams((p) => ({ ...p, page }));
    };

    const handleClearFilters = () => {
        setSearchTextInput("");
        setParams({ category, page: 1 });
        setFilters({});
    };

    const handleFiltersSubmit = (cat, filteredData) => {
        setParams({ ...filteredData, category: cat, page: 1 });
        setFilters(filteredData);
        setIsPopupOpen(false);
    };

    const handleExport = () => {
        dispatch(exportXLSModalActions.openModal({ params, filters }));
    };

    /* Table data */
    const tableData = {
        headLabels: HEAD_LABELS[category] || HEAD_LABELS.users,
        list:
            category === "users" ? <UserList users={rows} actions={true} /> :
            category === "receipts" ? <ReceiptsList receipts={rows} actions={false} /> :
            <PrizesList prizes={rows} />,
        data,
        actions: false,
        handleSortOptions: handleSort,
        handleClearSort,
        params,
    };

    const hasFilters = activeFilterCount > 0 || !!params.q;

    return (
        <>
            <ReactModal />
            {isExportXLSOpen && <ExportXLS />}
            {isUserDetailsOpen && <UserDetails />}
            {isWinnerDetailsOpen && <WinnerDetails />}
            {isReceiptViewOpen && <ReceiptView />}
            {isGameDetailsOpen && <GameDetails />}
            <FilterPopUp
                open={isPopupOpen}
                setIsOpen={setIsPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                handleFormSubmit={handleFiltersSubmit}
                category={category}
                handleFilters={setFilters}
                params={params}
            />

            <ListPageShell>
                <ListPageHeader
                    eyebrow={{ icon: <LuFileSearch size={18} />, label: "Raportet" }}
                    title="Raportet"
                    subtitle={subtitle}
                />

                <ReportsTabs value={category} onChange={handleTabChange} />

                {isStatistics ? (
                    <StatisticsPanel selectedGame={selectedGame} onSelectGame={setSelectedGame} />
                ) : (
                    <>
                        {/* <ListPageSearch
                            value={searchTextInput}
                            onChange={handleSearchTextInputChange}
                            placeholder="Kërko në rezultate..."
                            hasFilters={hasFilters}
                            onClearFilters={handleClearFilters}
                        /> */}

                        <ReportsToolbar
                            category={category}
                            scope={scope}
                            onScopeChange={handleScopeChange}
                            onOpenFilters={() => setIsPopupOpen(true)}
                            activeFilterCount={activeFilterCount}
                            onExport={handleExport}
                        />

                        {isFetching ? (
                            <div className="rounded-2xl border border-gray-200/80 bg-white py-24 flex items-center justify-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                                <CircularProgress style={{ color: "var(--secondary-color)" }} />
                            </div>
                        ) : isEmpty ? (
                            <ListPageEmpty
                                icon={FiSearch}
                                title={hasFilters ? "Asnjë rezultat nuk u gjet" : "Ende asnjë e dhënë"}
                                subtitle={
                                    hasFilters
                                        ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                                        : "Të dhënat e regjistruara do të shfaqen këtu."
                                }
                                action={
                                    hasFilters && (
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                                        >
                                            Pastro filtrat
                                        </button>
                                    )
                                }
                            />
                        ) : (
                            <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
                                {category === "business_winners" ? (
                                    <BusinessWinnersTable
                                        winners={rows}
                                        isLoading={isBusinessWinnersLoading || isBusinessWinnersFetching}
                                        params={params}
                                        onSort={handleSort}
                                    />
                                ) : (
                                    <DashboardTable tableData={tableData} />
                                )}
                            </div>
                        )}

                        {!isEmpty && !isFetching && (
                            <ListPager
                                currentPage={currentPage}
                                totalPages={totalPages || 1}
                                onChange={handlePageChange}
                                rangeLabel={rangeLabel}
                            />
                        )}
                    </>
                )}
            </ListPageShell>
        </>
    );
}
